import React, { useMemo } from 'react';
import { StyledTable, StyledTableContent, StyledTableHeader } from '../../table/StyledTable';
import { IWireDepositData } from 'types/order/escrow/IWireDepositData';
import { ICheckDepositData } from 'types/order/escrow/ICheckDeposit';
import { MatchedDepositRowContainer } from './MatchedDepositRowContainer';
import {
  convertAchDepositToExpectedDepositData,
  convertCheckDepositToExpectedDepositData,
  convertWireDepositToExpectedDepositData,
} from './MatchedDepositsType';
import { StyledIncomingTableRow } from './StyledDeposits';
import { IEscrowExpectedIncomingAchData } from 'types/accounting/data/IEscrowExpectedIncomingAchData';
import { IGetUser } from 'types/users/IUser';
import { useGetUsersByQueryQuery } from 'API/UserApi';
import { PageLoading } from 'ui-toolkit';
import { sortByUpdatedAtDescending } from '../../SortByCreatedAt';
import { useHasFeatureFlag } from 'selectors/useHasFeatureFlag';

interface IMatchedDepositsTableProps {
  achDeposits: IEscrowExpectedIncomingAchData[];
  wireDeposits?: IWireDepositData[];
  checkDeposits?: ICheckDepositData[];
}

export const MatchedDepositsTable: React.FC<IMatchedDepositsTableProps> = ({
  achDeposits,
  wireDeposits,
  checkDeposits,
}) => {
  const columns = ['Matched Date', 'Matched By', 'Sender', 'Type', 'Received', 'Status', ''];

  const usersQuery = useMemo(
    () =>
      achDeposits
        .flatMap((ach) => ach.matches || [])
        .map((ach) => ({ userType: 'ADMIN', userId: ach.createdBy } as IGetUser)) || [],
    [achDeposits]
  );
  const { data: users, isLoading: usersIsFetching } = useGetUsersByQueryQuery(usersQuery, {
    skip: usersQuery.length === 0,
  });
  const userIdToUserName = (id: string): string => {
    return (users || []).find((user) => user.id === id)?.name ?? '-';
  };
  const isSpruceWithZillowEnabled = useHasFeatureFlag('pinecone.spruce-with-zillow-closing');
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const achMatches = achDeposits?.flatMap((item) => convertAchDepositToExpectedDepositData(item, userIdToUserName, isSpruceWithZillowEnabled, isZillowClosingEnabled));
  const wireMatches = wireDeposits?.map((item) => convertWireDepositToExpectedDepositData(item, isSpruceWithZillowEnabled, isZillowClosingEnabled)) ?? [];
  const checkMatches = checkDeposits?.map((item) => convertCheckDepositToExpectedDepositData(item)) ?? [];

  const sortedDepositData = [...achMatches, ...wireMatches, ...checkMatches].sort(sortByUpdatedAtDescending);

  return (
    <PageLoading isLoading={usersIsFetching}>
      <StyledTable>
        <StyledTableHeader>
          <StyledIncomingTableRow columnsLength={columns.length}>
            {columns.map((t) => (
              <div className={'p-4 items-end'} key={t}>
                {t}
              </div>
            ))}
          </StyledIncomingTableRow>
        </StyledTableHeader>
        <StyledTableContent>
          {sortedDepositData.map((item, i) => (
            <MatchedDepositRowContainer index={i} depositData={item} key={item.updatedAt} />
          ))}
        </StyledTableContent>
      </StyledTable>
    </PageLoading>
  );
};
